"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";

/**
 * Petals drifting down behind a section's content — decoration only.
 *
 * The parent section needs `relative isolate`: the layer sits at z-index -1,
 * so it paints above the section background but below every word and card.
 * A section with its own absolutely positioned background photo passes a
 * `className` with a z-index between that photo and its content instead.
 *
 * Motion lives in globals.css (.petal-track / .petal); "reduce motion" hides
 * the petals completely. Petals only fall while their section is near the
 * viewport, so a page with many petal sections animates one or two at a time.
 * Only every other petal falls on a phone, where text runs edge to edge.
 */
const COLORS = ["#F0A8B0", "#E8BFA5", "#F4C6CC", "#D9AE8E"];

/**
 * Local preview only (operator, 18.10.2026: "vielleicht mit Herz?"): a build
 * made with NEXT_PUBLIC_PREVIEW_TOGGLES=1 shows hearts with ?herz (every third
 * petal) or ?herz=alle (all of them). Without that variable, as on Vercel, the
 * check is inlined as false and the petals stay as they are.
 */
const PREVIEW_TOGGLES = process.env.NEXT_PUBLIC_PREVIEW_TOGGLES === "1";
const HEART_PATH =
  "M10 17.6C3.3 12.9 0 9.3 0 5.4 0 2.4 2.3 0 5.2 0c2 0 3.8 1.1 4.8 2.8C11 1.1 12.8 0 14.8 0 17.7 0 20 2.4 20 5.4c0 3.9-3.3 7.5-10 12.2Z";

/** Small seeded generator: server and browser must produce identical markup. */
function seeded(seed: number) {
  let a = seed * 9973 + 17;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildPetals(seed: number, count: number) {
  const rnd = seeded(seed);
  return Array.from({ length: count }, (_, i) => {
    // Evenly spread across the width, then nudged, so petals never bunch up.
    const x = Math.min(97, Math.max(2, ((i + 0.5) / count) * 100 + (rnd() - 0.5) * 8));
    const fall = 11 + rnd() * 5;
    return {
      x: `${x.toFixed(1)}%`,
      size: Math.round(9 + rnd() * 4),
      fall: fall.toFixed(1),
      delay: (-rnd() * fall).toFixed(1),
      sway: (2.4 + rnd() * 1.5).toFixed(1),
      color: COLORS[(i + seed) % COLORS.length],
      opacity: (0.6 + rnd() * 0.25).toFixed(2),
      phone: i % 2 === 0,
    };
  });
}

export function FallingPetals({
  seed = 1,
  count = 8,
  className,
}: {
  /** Different per section, so no two sections show the same pattern. */
  seed?: number;
  count?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(true);
  const [hearts, setHearts] = useState<"none" | "mix" | "all">("none");

  useEffect(() => {
    if (!PREVIEW_TOGGLES) return;
    const v = new URLSearchParams(window.location.search).get("herz");
    // The URL is only known in the browser, after the server markup.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (v !== null) setHearts(v === "alle" ? "all" : "mix");
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), {
      rootMargin: "200px 0px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      data-active={active ? "true" : "false"}
      className={cn("petals pointer-events-none absolute inset-0 overflow-hidden", className ?? "-z-10")}
    >
      {buildPetals(seed, count).map((p, i) => (
        <span
          key={i}
          className={cn("petal-track", !p.phone && "hidden sm:block")}
          style={{ left: p.x, "--fall": `${p.fall}s`, "--delay": `${p.delay}s` } as CSSProperties}
        >
          <span className="petal" style={{ "--sway": `${p.sway}s` } as CSSProperties}>
            {hearts === "all" || (hearts === "mix" && i % 3 === 1) ? (
              <svg width={p.size + 2} height={Math.round((p.size + 2) * 0.9)} viewBox="0 0 20 18">
                <path d={HEART_PATH} fill={p.color} fillOpacity={p.opacity} />
              </svg>
            ) : (
              <svg width={p.size} height={Math.round(p.size * 1.35)} viewBox="0 0 20 27">
                <path
                  d="M10 0C17 6 20 14 15 21C12 25 8 25 5 21C0 14 3 6 10 0Z"
                  fill={p.color}
                  fillOpacity={p.opacity}
                />
                <path d="M10 3C9 10 9 16 10 23" stroke="#fff" strokeOpacity="0.4" strokeWidth="0.8" fill="none" />
              </svg>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Petals for a long area (an article body, the blog grid): only its top part
 * gets them. A track is as tall as its layer, so over a whole 8,000px article
 * a petal would race through in ten seconds instead of drifting.
 */
export function PetalsTop({ seed, height = "h-[1100px]" }: { seed: number; height?: string }) {
  return (
    <div aria-hidden className={cn("pointer-events-none absolute inset-x-0 top-0 -z-10", height)}>
      <FallingPetals seed={seed} className="z-0" />
    </div>
  );
}
